import type { AppLocale } from "../locales";
import { authApi } from "./authApi";
import { clinicApi } from "./clinic";
import { difficultyOptionsApi } from "./difficultyOptions";
import { legalPageApi } from "./legal-page";
import { localeCopyApi } from "./localeCopy";
import { onboardingApi } from "./onboarding";
import { patientApi } from "./patient";
import { releaseNotesApi } from "./releaseNotes";
import { sessionReviewApi } from "./sessionOverview";
import { apiEndpoints, cmsEndpoints, cmsService, httpService, type CmsVersion } from "./shared";
import { soundApi } from "./sound";
import { templateApi } from "./templates";
import { therapistApi } from "./therapist";
import { therapySessionApi } from "./therapySession";
import { treatmentApi } from "./treatment";

export type ApiConfigurationCreation = {
  baseUrl: string;
  cmsAccessToken: string;
  getAccessToken: (() => Promise<string | undefined>) | undefined;
  locale: AppLocale;
  cmsVersion: CmsVersion;
  origin?: string;
  localCms?: boolean;
  cmsLinkWithEnv?: string;
}

type KeyPaths<T extends Record<string, unknown>> = {
  // @ts-expect-error just a type helper
  [K in keyof T]: T[K] extends Record<string, unknown> ? `${K & string}.${KeyPaths<T[K]> & string}` : K;
}[keyof T];

export type ApiEndpoints = ReturnType<typeof apiEndpoints>;
export type CmsEndpoints = ReturnType<typeof cmsEndpoints>;

export type ApiEndpointKey = KeyPaths<ApiEndpoints>;

export type ApiConfiguration = {
  baseUrl: string;
  cmsAccessToken: string;
  endpoints: ApiEndpoints;
  cmsEndpoints: CmsEndpoints;
  getAccessToken: (() => Promise<string | undefined>) | undefined;
  locale: AppLocale;
  cmsService: ReturnType<typeof cmsService>;
  httpService: ReturnType<typeof httpService>;
  origin?: string;
}

const api = (config: ApiConfiguration) => ({
  auth: authApi(config),
  localeCopy: localeCopyApi(config),
  legalPage: legalPageApi(config),
  sound: soundApi(config),
  patient: patientApi(config),
  treatment: treatmentApi(config),
  template: templateApi(config),
  difficultyOptions: difficultyOptionsApi(config),
  therapist: therapistApi(config),
  clinic: clinicApi(config),
  onboarding: onboardingApi(config),
  therapySession: therapySessionApi(config),
  sessionReview: sessionReviewApi(config),
  releaseNotes: releaseNotesApi(config),
});

export type Api = ReturnType<typeof api>;

export const configureApiEndpoints = (configCreation: ApiConfigurationCreation) => apiEndpoints(configCreation);

export const configureApi = (configCreation: ApiConfigurationCreation) => {
  const config: ApiConfiguration = {
    baseUrl: configCreation.baseUrl,
    cmsAccessToken: configCreation.cmsAccessToken,
    endpoints: configureApiEndpoints(configCreation),
    cmsEndpoints: cmsEndpoints(),
    locale: configCreation.locale,
    getAccessToken: configCreation.getAccessToken,
    cmsService: cmsService({
      cmsAccessToken: configCreation.cmsAccessToken,
      cmsVersion: configCreation.cmsVersion,
      locale: configCreation.locale,
      useLocalCms: configCreation.localCms,
      cmsLinkWithEnv: configCreation.cmsLinkWithEnv,
    }),
    httpService: httpService({
      getAccessToken: configCreation.getAccessToken,
      locale: configCreation.locale,
      origin: configCreation.origin,
    }),
  };
  return api(config);
};

export * from "./clinic";
export * from "./onboarding";
export * from "./patient";
export * from "./therapist";
export * from "./useApi";
export * from "./legal-page";
export * from "./QueryKeys";
export * from "./shared";
export * from "./websocket";
export * from "./useFetchExerciseDetails";
export * from "./useFetchAllExerciseNames";
export * from "./therapySession";
export * from "./useFetchPoseSequence";
export * from "./useFetchMistakesVideos";
export * from "./useFetchMistakesText";
